import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { Event } from '@domain/events/models/Event';

export const useProductEvents = (productId?: string) => {
    const queryClient = useQueryClient();

    const {
        data: events,
        isLoading: isLoadingEvents,
        error: eventsError
    } = useQuery<Event[]>({
        queryKey: ['product-events', productId],
        queryFn: async () => {
            if (!productId) throw new Error('Product ID is required');
            const result = await container.eventRepository.getProductEvents(productId);
            if (result.status === 'error') {
                throw new Error(result.error.message);
            }
            return result.data;
        },
        enabled: !!productId,
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    const linkEventMutation = useMutation({
        mutationFn: async (eventId: string) => {
            if (!productId) throw new Error('Product ID is required');
            const result = await container.eventRepository.linkProduct(eventId, [productId]);
            if (result.status === 'error') {
                throw new Error(result.error.message);
            }
            return result.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product-events', productId] });
            queryClient.invalidateQueries({ queryKey: ['event-products'] });
        }
    });

    const unlinkEventMutation = useMutation({
        mutationFn: async (eventId: string) => {
            if (!productId) throw new Error('Product ID is required');
            const result = await container.eventRepository.unlinkProduct(eventId, [productId]);
            if (result.status === 'error') {
                throw new Error(result.error.message);
            }
            return result.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product-events', productId] });
            queryClient.invalidateQueries({ queryKey: ['event-products'] });
        }
    });

    return {
        events,
        isLoading: isLoadingEvents,
        error: eventsError,
        linkEvent: linkEventMutation.mutateAsync,
        unlinkEvent: unlinkEventMutation.mutateAsync,
        isLinking: linkEventMutation.isPending,
        isUnlinking: unlinkEventMutation.isPending,
    };
};

